/**
 * How long a ban lasts, and how long a reason may be.
 *
 * The durations are a closed list rather than a number of minutes an operator types, because a
 * ban is a decision made in a hurry about somebody who is ruining a match right now: a picker
 * with five entries is one click, and it cannot produce the 43-year ban that a mistyped
 * milliseconds field would. Shared rather than server-local for the ordinary reason — the
 * console draws the picker from this list and the server validates against it, and two copies
 * would let the browser offer a duration the API refuses.
 *
 * `expiresAtFrom` is the only place a duration becomes an instant. Everything downstream reads
 * `user_bans.expires_at` and compares it to `now()`, which is what makes expiry *computed*: a
 * ban ends because that timestamp has passed, with nothing to sweep and no cron to forget to
 * run.
 */

/** One entry in the picker. `ms` is null for the permanent ban, which never grows an expiry. */
export interface BanDuration {
  /** Stored verbatim in `user_bans.duration`, so a row says which button was pressed. */
  id: string;
  /** What the console draws. */
  label: string;
  /** How long the ban lasts, or null for permanent. */
  ms: number | null;
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/** The durations a moderator may pick, shortest first. Permanent is last because it is the big one. */
export const BAN_DURATIONS = [
  { id: '1h', label: '1 hour', ms: HOUR_MS },
  { id: '24h', label: '24 hours', ms: 24 * HOUR_MS },
  { id: '7d', label: '7 days', ms: 7 * DAY_MS },
  { id: '30d', label: '30 days', ms: 30 * DAY_MS },
  { id: 'permanent', label: 'Permanent', ms: null },
] as const satisfies readonly BanDuration[];

export type BanDurationId = (typeof BAN_DURATIONS)[number]['id'];

/** The ids alone — the enum the API validates against, built from the table so they cannot drift. */
export const BAN_DURATION_IDS = BAN_DURATIONS.map((entry) => entry.id) as [
  BanDurationId,
  ...BanDurationId[],
];

/** The entry an id names, or undefined for an id this build does not know. */
export function banDuration(id: string): BanDuration | undefined {
  return BAN_DURATIONS.find((entry) => entry.id === id);
}

/**
 * When a ban of this duration, issued now, stops applying — or null when it never does.
 *
 * An unknown id answers null rather than throwing. That is the safe direction only because the
 * API validates the id before it gets here: this function is the *arithmetic*, and the schema is
 * the gate.
 */
export function expiresAtFrom(id: string, fromMs: number): number | null {
  const entry = banDuration(id);
  if (entry === undefined || entry.ms === null) return null;
  return fromMs + entry.ms;
}

/**
 * The reason a moderator types. Required, and short enough to read in a list — it is shown to
 * the banned player and to every other operator, so it is a sentence rather than an essay.
 */
export const BAN_REASON_MIN_LENGTH = 3;
export const BAN_REASON_MAX_LENGTH = 200;
